import React, { useContext, useEffect, useState } from 'react';
import { createManageChannelBreadcrumbs } from '../Utils/BreadcrumbUtils';
import { Breadcrumb } from '../Components/Breadcrumb';
import DetailSection from '../Domain/DetailSection';
import CustomSearchBar from 'Components/GMS/Products/components/CustomSearchBar';
import { CREATE_CHANNEL, CREATE_CHANNEL_PERMISSION_MESSAGE, CREATE_PERMISSION, DELETE_PERMISSION, READ_PERMISSION, UPDATE_DOMAIN_PERMISSION_MESSAGE, UPDATE_PERMISSION } from '../Utils/Constants';
import ChannelTable from './ChannelTable';
import { CreateChannelDialog } from './CreateChannelDialog';
import { useHistory } from 'react-router-dom';
import { TIndustry, TIndustryUpdatePayload } from '../Types/domain.types';
import { TChannel, TCreateParentChannelPayload } from '../Types/channel.types';
import { toast } from 'react-toastify';
import Paginator from 'Components/Common/PaginatorWithApi';
import { TPaginationParams } from 'Pages/GMS/Performance/analytics';
import { CreateDomainDialog } from '../Domain/CreateDomainDialog';
import NoDataComponent from 'Components/Common/NoDataComponent';
import { AppContext } from 'Context/AppContext';
import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';
import AppSettings from 'Settings/AppSettings';

const api_base_url = AppSettings.GMS_BACKEND_API_URL;

type TChannelProps = {
    partyId?: string;
    domainId?: string;
}

const ManageChannel = ({partyId, domainId}: TChannelProps) => {
    const history = useHistory();
    const appContext = useContext(AppContext);
    
    const readPermission = appContext.pm.checkPermission(READ_PERMISSION);
    const createPermission = appContext.pm.checkPermission(CREATE_PERMISSION);
    const updatePermission = appContext.pm.checkPermission(UPDATE_PERMISSION);
    const deletePermission = appContext.pm.checkPermission(DELETE_PERMISSION);

    const [domainData, setDomainData] = useState<TIndustry | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isCreateChannelOpen, setIsCreateChannelOpen] = useState(false);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [paginationParams, setPaginationParams] = useState<TPaginationParams>({pageNumber: 1, pageSize: 10});
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [isCreatingChannel, setIsCreatingChannel] = useState(false);
    const [refreshChannelTable, setRefreshChannelTable] = useState(0);
    const [isEditDomainOpen, setIsEditDomainOpen] = useState(false);
    const [isUpdatingDomain, setIsUpdatingDomain] = useState(false);
    const [channels, setChannels] = useState<TChannel[]>([]);
    const [isChannelsLoading, setIsChannelsLoading] = useState(true);
    
    useEffect(() => {
        const fetchData = async () => {
            if (!partyId || !domainId) {
                setIsLoading(false);
                return;
            }
            try {
                setIsLoading(true);
                const response = await ServiceWrapper.doGet( `${api_base_url}party/${partyId}/industry/${domainId}`, null, null, AuthManager.getGmsToken(),); 
                const domainResponse = response?.data;
                if (domainResponse && domainResponse.done && domainResponse.data) {
                    setDomainData(domainResponse.data[0] ?? []);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Failed to load details');
            } finally {
                setIsLoading(false);
            }
        };
        if (!readPermission) {
            history.push("/workspace/module/permission/denied");
            return;
        } else {
            fetchData();
        }
    }, [partyId, domainId]);

    useEffect(() => {
        if (refreshChannelTable > 0) {
            setIsChannelsLoading(true);
        }
    }, [refreshChannelTable]);

    const handleNavigateToParties = () => {
        history.push('/admin/party/manage');
    };
    
    const handleNavigateToDomain = () => {
        if (partyId) {
            history.push(`/admin/party/domain/${partyId}`);
        }
    };    
    
    const breadcrumbItems =  domainData
        ? createManageChannelBreadcrumbs(domainData.party_name, domainData.name, handleNavigateToParties, handleNavigateToDomain)
        : [];

    const handleEditDomain = () => {
        if (!updatePermission) {
            toast.warn(UPDATE_DOMAIN_PERMISSION_MESSAGE);
            return;
        }  
        setIsEditDomainOpen(true);
    };

    const handleUpdateDomain = async (formData: any) => {
        if (!domainData || !domainId) return;
        
        setIsUpdatingDomain(true);
        
        try {
            const payload: TIndustryUpdatePayload = {
                active: true,
                description: formData.description || '',
                id: domainId,
                name: formData.domainName,
                sams_id: domainData.sams_id
            };
            
            const domainResponse = await ServiceWrapper.doPost( `${api_base_url}industry/edit/${domainId}`, payload, null, AuthManager.getGmsToken(),);
            const response = domainResponse?.data;
            if (response && response.done) {
                toast.success('Domain updated successfully!');
                
                setDomainData({
                    ...domainData,
                    name: formData.domainName,
                    description: formData.description || ''
                });
                
                setIsEditDomainOpen(false);
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to update domain');
            }
        } catch (error) {
            console.error('Error updating domain:', error);
            toast.error('An error occurred while updating the domain');
        } finally {
            setIsUpdatingDomain(false);
        }
    };

    const handleCreateChannel = async (formData: any) => {
        if (!createPermission) {
            toast.warn(CREATE_CHANNEL_PERMISSION_MESSAGE);
            return;
        }  
        if (!partyId || !domainId) return;
        
        setIsCreatingChannel(true);
        
        try {
            const payload: TCreateParentChannelPayload = {
                name: formData.channelName,
                party_id: partyId,
                industry_id: domainId,
                channel_ids: []
            };
            
            const channelResponse = await ServiceWrapper.doPost( `${api_base_url}parent_channel`, payload, null, AuthManager.getGmsToken(),);
            const response = channelResponse?.data;
            if (response && response.done) {
                toast.success('Channel created successfully!');
                setIsCreateChannelOpen(false);
                
                setRefreshChannelTable(prev => prev + 1);
                
                setPaginationParams(prev => ({ ...prev, pageNumber: 1 }));
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to create channel');
            }
        } catch (error) {
            console.error('Error creating channel:', error);
            toast.error('An error occurred while creating the channel');
        } finally {
            setIsCreatingChannel(false);
        }
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setPaginationParams(prev => ({ ...prev, pageNumber: 1 }));
    };

    const handlePageChange = (pageNumber: number) => {
        setPaginationParams((prevState: TPaginationParams) => ({
            ...prevState,
            pageNumber: pageNumber,
        }));
    };
        
    const updatePageSize = (pageSize: number) => {
        setPaginationParams((prevState: TPaginationParams) => ({
            ...prevState,
            pageSize: pageSize,
            pageNumber: 1
        }));
    };

    if (isLoading) {
        return (
            <div className="tw-flex tw-justify-center tw-items-center tw-h-64">
                <div className="tw-inline-flex tw-items-center">
                    <svg className="tw-animate-spin tw-h-5 tw-w-5 tw-mr-3 tw-text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading details...
                </div>
            </div>
        );
    }

    if (!domainData) {
        return (
            <div className="tw-text-center tw-py-8 tw-text-gray-500">
                Data not found
            </div>
        );
    }

    const onCreateChannelClick = () => {
        if (!createPermission) {
            toast.warn(CREATE_CHANNEL_PERMISSION_MESSAGE);
            return;
        }
        setIsCreateChannelOpen(true);
    }

    return (
        <>
            {breadcrumbItems.length > 0 && <Breadcrumb items={breadcrumbItems} />}
            <DetailSection 
                config={{
                    type: 'domain',
                    data: {
                        title: domainData.name,
                        partyName: domainData.party_name,
                        description: domainData.description || 'N/A'
                    },
                    onEdit: handleEditDomain
                }}
            />
            {!isChannelsLoading && channels.length === 0 ? (
                <>
                    <NoDataComponent title='No Channel found' />
                    <div className="tw-flex tw-justify-center">
                        <button onClick={onCreateChannelClick} className={`button-style tw-w-max tw-flex tw-justify-center`}>
                            {CREATE_CHANNEL}
                        </button>
                    </div>
                </>
            ) : (
                <>
                    {channels.length !== 0 && (
                        <div className={`tw-flex tw-items-center tw-justify-between tw-mt-2`}>
                            <div className='tw-w-[25%] tw-mt-4'>
                                <CustomSearchBar 
                                    handleSearchChange={handleSearchChange} 
                                    placeholder='Search channels' 
                                />
                            </div>
                            <button onClick={onCreateChannelClick} className={`button-style tw-w-max`}>
                                {CREATE_CHANNEL}
                            </button>
                        </div>
                    )}
                    {partyId && domainId && (
                        <ChannelTable 
                            partyId={partyId}
                            domainId={domainId}
                            searchTerm={searchTerm}
                            showLoader={showLoader}
                            channels={channels}
                            setChannels={setChannels}
                            setShowLoader={setShowLoader}
                            setTotalRecords={setTotalRecords}
                            paginationParams={paginationParams}
                            refreshTrigger={refreshChannelTable}
                            onChannelsLoaded={() => setIsChannelsLoading(false)}
                            readPermission={readPermission}
                            createPermission={createPermission}
                            updatePermission={updatePermission}
                            deletePermission={deletePermission}
                        />
                    )}
                </>
            )}
            {!showLoader && channels.length !== 0 && totalRecords > paginationParams.pageSize ?
                <div id="pagination-container" className="tw-py-2">
                    <div className={`tw-flex tw-justify-end tw-items-center tw-space-x-3 tw-flex-wrap`}>
                        <div className={`tw-flex tw-items-center tw-space-x-3`}>
                            <span>Show</span>
                            <select
                                id="page-size-select"
                                className={`tw-border focus:tw-outline-none tw-p-1`}
                                style={{ width: "80px" }}
                                aria-label="Records on page select"
                                value={paginationParams?.pageSize ?? 10}
                                onChange={(e) => {
                                    updatePageSize(parseInt(e?.target?.value));
                                }}
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                            </select>
                        </div>
                        <Paginator
                            onPageChange={handlePageChange}
                            recordPerPage={paginationParams?.pageSize}
                            presentPage={paginationParams?.pageNumber}
                            totalRecords={totalRecords} 
                        />
                    </div>
                </div> : null}
            <CreateChannelDialog 
                isOpen={isCreateChannelOpen}
                onClose={() => setIsCreateChannelOpen(false)}
                onSave={handleCreateChannel}
                isLoading={isCreatingChannel}
            />
            <CreateDomainDialog
                isOpen={isEditDomainOpen}
                onClose={() => setIsEditDomainOpen(false)}
                onSave={handleUpdateDomain}
                isLoading={isUpdatingDomain}
                editData={domainData}
                mode="edit"
            />
        </>
    );
}

export default ManageChannel;